import React, { useEffect, useMemo, useRef, useState } from 'react';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControlLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  Tab,
  TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  getGlobalServiceCitiesApi,
  getTenantDraftApi,
  saveTenantDraftApi,
  uploadTenantDraftLogoApi,
  promoteTenantDraftApi,
} from 'services/TenantService';
import {
  DEFAULT_COLORS,
  TENANT_FORM_TABS,
  tabForField,
  initialFormState,
  validateForm,
  errorsByTab,
  formToDraftPayload,
  draftPayloadToForm,
  backendFieldToFormField,
  emptyAdmin,
} from './tenantFormConfig';

// The tabbed Create Tenant form for a single draft.
//
// Two validation modes, per the requirement:
//   • Save (draft)   — LENIENT. Persists whatever is entered, any state; never
//                      creates a tenant. AUTO-SAVES ~2s after you stop typing
//                      (and on tab-switch / logo upload / explicit "Save now"),
//                      so closing the tab never loses work.
//   • Submit (promote) — STRICT. Full validation; on error, the offending tabs
//                      are badged with a count, the form jumps to the first bad
//                      tab and focuses the first bad field. A promote 422 from
//                      the backend feeds the same navigation.
//
// Props: draftId (string), onProvisioned(result) — tenant created.
function TenantFormTabs({ draftId, onProvisioned }) {
  const [form, setForm] = useState(initialFormState);
  // Validation is computed live from `form`; what the user SEES is gated so a
  // field only shows its error once it's been blurred (touched) or a submit
  // asked to reveal everything. serverErrors carries messages only the backend
  // knows (e.g. subdomain already taken) until the field is edited again.
  const [touched, setTouched] = useState({}); // { formErrorKey: true }
  const [showAllErrors, setShowAllErrors] = useState(false);
  const [serverErrors, setServerErrors] = useState({}); // { formErrorKey: msg }
  const [activeTab, setActiveTab] = useState('identity');
  const [globalCities, setGlobalCities] = useState([]);
  const [selectedCities, setSelectedCities] = useState([]);
  const [citiesLoadError, setCitiesLoadError] = useState('');
  const [logo, setLogo] = useState(null); // { url, name } — staged server-side
  const [logoUploading, setLogoUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  // dirty = there are edits not yet persisted. Drives the auto-save debounce
  // and the header status ("Unsaved changes" → "Saving…" → "All changes saved").
  const [dirty, setDirty] = useState(false);

  // Refs to focus the first invalid field on a tab.
  const fieldRefs = useRef({});
  const registerRef = (field) => (el) => {
    if (el) fieldRefs.current[field] = el;
  };

  // Latest values for the debounced auto-save timer (avoids stale closures).
  const formRef = useRef(form);
  const citiesRef = useRef(selectedCities);
  const activeTabRef = useRef(activeTab);
  const dirtyRef = useRef(false);
  const savingRef = useRef(false);
  formRef.current = form;
  citiesRef.current = selectedCities;
  activeTabRef.current = activeTab;
  dirtyRef.current = dirty;

  const markDirty = () => setDirty(true);

  // ---- Load draft + city canon on mount --------------------------------
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      getGlobalServiceCitiesApi().catch(() => null),
      getTenantDraftApi(draftId).catch(() => null),
    ]).then(([citiesRes, draftRes]) => {
      if (cancelled) return;
      const cities = citiesRes?.data || [];
      setGlobalCities(cities);
      if (!citiesRes) {
        setCitiesLoadError(
          'Could not load the city list — retry, or continue and map cities later',
        );
      }
      const draft = draftRes?.data;
      if (draft) {
        setForm(draftPayloadToForm(draft.payload));
        const ids = draft.payload?.cities || [];
        setSelectedCities(cities.filter((c) => ids.includes(c.id)));
        if (draft.branding?.logo?.url) {
          setLogo({ url: draft.branding.logo.url, name: draft.branding.logo.name });
        }
        if (draft.lastTabKey && TENANT_FORM_TABS.some((t) => t.key === draft.lastTabKey)) {
          setActiveTab(draft.lastTabKey);
        }
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [draftId]);

  // ---- Auto-save: persist ~1.5s after the last edit --------------------
  // Debounced so we don't hammer the API on every keystroke; the `dirty` flag
  // (set by every change handler, cleared on a clean save) is the trigger.
  useEffect(() => {
    if (!dirty || saving) return undefined;
    const timer = setTimeout(() => {
      persist().catch(() => {
        // Leave dirty=true so the next edit (or explicit Save) retries;
        // don't toast on silent autosave failures.
      });
    }, 1500);
    return () => clearTimeout(timer);
    // Re-arm whenever the content changes while dirty.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, selectedCities, dirty, saving]);

  // Safety nets: warn on tab-close with unsaved edits, and flush on unmount
  // (e.g. navigating away via the sidebar) so nothing is lost.
  useEffect(() => {
    const beforeUnload = (e) => {
      if (dirtyRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => {
      window.removeEventListener('beforeunload', beforeUnload);
      // Best-effort flush of pending edits when leaving the form.
      if (dirtyRef.current && !savingRef.current) {
        saveTenantDraftApi(draftId, {
          payload: formToDraftPayload(formRef.current, citiesRef.current),
          lastTabKey: activeTabRef.current,
        }).catch(() => {});
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftId]);

  // Live validation, computed from the current form (single source of truth).
  const liveErrors = useMemo(() => validateForm(form, Boolean(logo)), [form, logo]);

  // What the user actually SEES: a live error is shown only once its field is
  // touched (blurred) or a submit revealed everything; server-only messages
  // (e.g. subdomain taken) show until that field is edited. On-blur real-time.
  const errors = useMemo(() => {
    const shown = {};
    Object.entries(liveErrors).forEach(([key, msg]) => {
      if (showAllErrors || touched[key]) shown[key] = msg;
    });
    Object.entries(serverErrors).forEach(([key, msg]) => {
      if (msg) shown[key] = msg;
    });
    return shown;
  }, [liveErrors, touched, showAllErrors, serverErrors]);

  const tabErrorCounts = useMemo(() => errorsByTab(errors), [errors]);

  // Reveal a field's error once the user leaves it (blur).
  const markTouched = (key) => setTouched((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
  // Clear a stale server-only message when the field is edited again.
  const clearServerError = (key) =>
    setServerErrors((prev) => (prev[key] ? { ...prev, [key]: '' } : prev));

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
    clearServerError(field);
    markDirty();
  };

  // ---- Admins (dynamic array) ------------------------------------------
  const handleAdminChange = (index, sub) => (event) => {
    const value = event.target.value;
    setForm((prev) => {
      const admins = prev.admins.map((a, i) => (i === index ? { ...a, [sub]: value } : a));
      return { ...prev, admins };
    });
    clearServerError(`admins.${index}.${sub}`);
    markDirty();
  };

  const addAdmin = () => {
    setForm((prev) => ({ ...prev, admins: [...prev.admins, emptyAdmin()] }));
    markDirty();
  };

  const removeAdmin = (index) => {
    setForm((prev) => {
      const admins = prev.admins.filter((_, i) => i !== index);
      return { ...prev, admins: admins.length ? admins : [emptyAdmin()] };
    });
    markDirty();
  };

  // ---- Draft save (lenient) --------------------------------------------
  // Reads the LATEST state via refs so the debounced timer never persists a
  // stale snapshot. Clears the dirty flag and stamps lastSavedAt on success.
  const persist = async (tabKey) => {
    savingRef.current = true;
    setSaving(true);
    // Snapshot what we're about to save so we can tell if edits landed while
    // the request was in flight (then we stay dirty and re-save).
    const saved = { form: formRef.current, cities: citiesRef.current };
    try {
      await saveTenantDraftApi(draftId, {
        payload: formToDraftPayload(saved.form, saved.cities),
        lastTabKey: tabKey || activeTabRef.current,
      });
      setLastSavedAt(Date.now());
      // Only clear dirty if nothing changed mid-flight.
      if (formRef.current === saved.form && citiesRef.current === saved.cities) {
        setDirty(false);
      }
      return true;
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  // Index of the current tab, and the next tab key (null if already last).
  const currentTabIndex = TENANT_FORM_TABS.findIndex((t) => t.key === activeTab);
  const nextTabKey =
    currentTabIndex >= 0 && currentTabIndex < TENANT_FORM_TABS.length - 1
      ? TENANT_FORM_TABS[currentTabIndex + 1].key
      : null;

  // Per-tab "Save & Next": persist the draft, then advance to the next tab.
  const handleSaveAndNext = async () => {
    try {
      await persist(nextTabKey || activeTab);
      if (nextTabKey) setActiveTab(nextTabKey);
    } catch (e) {
      toast.error('Could not save the draft. Please try again.');
    }
  };

  // Autosave silently when switching tabs, so nothing is lost.
  const handleTabChange = async (_event, newValue) => {
    setActiveTab(newValue);
    if (dirtyRef.current) {
      persist(newValue).catch(() => {
        // Non-blocking: the debounce/explicit Save will retry.
      });
    }
  };

  // Jump straight to a tab (used by the Review tab's per-section edit icons).
  const goToTab = (tabKey) => {
    setActiveTab(tabKey);
    if (dirtyRef.current) {
      persist(tabKey).catch(() => {});
    }
  };

  // ---- Logo upload (staged immediately to the draft) -------------------
  const handleLogoSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setLogoUploading(true);
    try {
      // Persist current text state first so the draft row exists/updates,
      // then stage the file against it.
      await persist();
      const res = await uploadTenantDraftLogoApi(draftId, file);
      setLogo({ url: res?.data?.url, name: res?.data?.name });
      // liveErrors recomputes without the logo error once `logo` is set.
      toast.success('Logo uploaded');
    } catch (e) {
      toast.error('Logo upload failed. Please try again.');
    } finally {
      setLogoUploading(false);
    }
  };

  // ---- Submit (strict promote) -----------------------------------------
  // Focus the first invalid field, switching to its tab first.
  const focusFirstError = (errMap) => {
    const firstField = Object.keys(errMap)[0];
    if (!firstField) return;
    const tab = tabForField(firstField);
    if (tab && tab !== activeTab) setActiveTab(tab);
    // Wait for the tab panel to mount, then focus.
    setTimeout(() => {
      const el = fieldRefs.current[firstField];
      if (el) {
        el.focus?.();
        el.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      }
    }, 60);
  };

  const handleSubmit = async () => {
    // Client-side strict validation first (instant, offline of the server).
    const clientErrors = validateForm(form, Boolean(logo));
    if (Object.keys(clientErrors).length) {
      // Reveal every error (submit overrides the on-blur gating).
      setShowAllErrors(true);
      focusFirstError(clientErrors);
      toast.error('Some fields need attention — see the highlighted tabs');
      return;
    }
    setSubmitting(true);
    try {
      // Save the latest state, then promote.
      await persist();
      const res = await promoteTenantDraftApi(draftId);
      if (res?.success) {
        toast.success('Tenant created successfully');
        onProvisioned?.(res.data);
        return;
      }
      // Structured 422 — map backend fieldErrors onto tabs/fields. These are
      // server-only judgements (e.g. subdomain taken) so they go into
      // serverErrors and show until the field is edited.
      const fieldErrors = res?.fieldErrors || [];
      if (fieldErrors.length) {
        const mapped = {};
        fieldErrors.forEach((fe) => {
          const ff = backendFieldToFormField(fe.field) || fe.field;
          mapped[ff] = fe.message;
        });
        setServerErrors(mapped);
        focusFirstError(mapped);
        toast.error(res?.message || 'Some fields need attention before this tenant can be created');
      } else {
        toast.error(res?.message || 'Could not create the tenant.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to create tenant. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Fields that validate as a group — blurring one reveals errors on all, so
  // cross-field rules (PAN↔GSTIN match, SMTP all-or-nothing) surface together.
  const BLUR_COMPANIONS = {
    invGstin: ['invPan'],
    invPan: ['invGstin'],
    smtpHost: ['smtpPort', 'smtpUsername', 'smtpPassword', 'smtpFromEmail'],
    smtpPort: ['smtpHost', 'smtpUsername', 'smtpPassword', 'smtpFromEmail'],
    smtpUsername: ['smtpHost', 'smtpPort', 'smtpPassword', 'smtpFromEmail'],
    smtpPassword: ['smtpHost', 'smtpPort', 'smtpUsername', 'smtpFromEmail'],
    smtpFromEmail: ['smtpHost', 'smtpPort', 'smtpUsername', 'smtpPassword'],
  };
  const handleBlur = (key) => () => {
    markTouched(key);
    (BLUR_COMPANIONS[key] || []).forEach(markTouched);
  };

  // ---- Field helpers (identical styling to the old CreateTenant) -------
  const textField = (field, label, options = {}) => (
    <TextField
      fullWidth
      size="small"
      label={label}
      value={form[field]}
      onChange={handleChange(field)}
      onBlur={handleBlur(field)}
      error={Boolean(errors[field])}
      helperText={errors[field] || options.hint || ''}
      required={options.required}
      placeholder={options.placeholder}
      type={options.type}
      inputRef={registerRef(field)}
    />
  );

  // A field inside admin block `index`. Keyed/ref'd by admins.<index>.<sub> so
  // validation errors and focus-navigation resolve to the right block.
  const adminField = (index, sub, label, options = {}) => {
    const key = `admins.${index}.${sub}`;
    return (
      <TextField
        fullWidth
        size="small"
        label={label}
        value={form.admins[index]?.[sub] ?? ''}
        onChange={handleAdminChange(index, sub)}
        onBlur={handleBlur(key)}
        error={Boolean(errors[key])}
        helperText={errors[key] || options.hint || ''}
        required={options.required}
        inputRef={registerRef(key)}
      />
    );
  };

  const colorField = (field, label) => {
    const value = form[field].trim();
    const pickerValue = /^#[0-9a-fA-F]{6}$/.test(value) ? value : DEFAULT_COLORS[field];
    return (
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <input
          type="color"
          value={pickerValue}
          onChange={(event) => handleChange(field)({ target: { value: event.target.value } })}
          aria-label={`${label} picker`}
          style={{
            width: 40,
            height: 40,
            padding: 2,
            border: '1px solid #c4c4c4',
            borderRadius: 4,
            background: '#fff',
            cursor: 'pointer',
            flexShrink: 0,
          }}
        />
        {textField(field, label)}
      </div>
    );
  };

  // Live brand preview (kept from the original one-page form).
  const preview = () => {
    const safe = (v, d) => (/^#[0-9a-fA-F]{3,6}$/.test(v.trim()) ? v.trim() : d);
    const primary = safe(form.primaryColor, DEFAULT_COLORS.primaryColor);
    const secondary = safe(form.secondaryColor, DEFAULT_COLORS.secondaryColor);
    const accent = safe(form.accentColor, DEFAULT_COLORS.accentColor);
    const legend = (swatch, title, text) => (
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        <span
          style={{
            width: 14,
            height: 14,
            borderRadius: 3,
            background: swatch,
            border: '1px solid rgba(0,0,0,0.15)',
            marginTop: 2,
            flexShrink: 0,
          }}
        />
        <span style={{ fontSize: 12, color: '#444' }}>
          <strong>{title}</strong> — {text}
        </span>
      </div>
    );
    return (
      <div style={{ border: '1px solid #e0e0e0', borderRadius: 8, overflow: 'hidden' }}>
        <div
          style={{
            background: primary,
            color: '#fff',
            padding: '8px 14px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: 13,
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600 }}>
            {logo?.url && (
              <img
                src={logo.url}
                alt=""
                style={{
                  height: 20,
                  width: 20,
                  objectFit: 'contain',
                  borderRadius: 3,
                  background: '#fff',
                }}
              />
            )}
            {form.name.trim() || 'Tenant'} portal
          </span>
          <span
            style={{
              background: '#fff',
              color: primary,
              borderRadius: 4,
              padding: '3px 10px',
              fontSize: 11,
              fontWeight: 600,
            }}
          >
            + New booking
          </span>
        </div>
        <div style={{ padding: 14, background: '#fafafa' }}>
          <div
            style={{
              background: accent,
              color: '#0B2545',
              borderRadius: 4,
              padding: '5px 10px',
              fontSize: 11,
              marginBottom: 10,
              display: 'inline-block',
            }}
          >
            Highlight / alert banner
          </div>
          <div style={{ fontSize: 12, color: '#333', marginBottom: 6 }}>Trips completed</div>
          <div
            style={{
              background: '#e6e6e6',
              borderRadius: 6,
              height: 10,
              overflow: 'hidden',
              marginBottom: 12,
            }}
          >
            <div style={{ width: '62%', height: '100%', background: secondary }} />
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            {legend(primary, 'Primary', 'top bar, table headers, buttons and links')}
            {legend(
              secondary,
              'Secondary',
              'progress bars, positive states and secondary surfaces',
            )}
            {legend(accent, 'Accent', 'alert banners, highlights and status flags')}
          </div>
        </div>
      </div>
    );
  };

  // Tab label with an inline error-count chip. Kept INSIDE the tab's flow (not
  // an absolutely-positioned Badge) so it can never be clipped by the tab edge.
  const tabLabel = (tab) => {
    const count = tabErrorCounts[tab.key] || 0;
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          maxWidth: '100%',
          minWidth: 0,
        }}
      >
        <span className="tabLabelText" style={{ minWidth: 0 }}>
          {tab.label}
        </span>
        {count > 0 && (
          <span
            aria-label={`${count} field${count > 1 ? 's' : ''} need attention`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 18,
              height: 18,
              padding: '0 5px',
              borderRadius: 9,
              background: '#d32f2f',
              color: '#fff',
              fontSize: 11,
              fontWeight: 700,
              lineHeight: 1,
              flexShrink: 0,
            }}
          >
            {count}
          </span>
        )}
      </span>
    );
  };

  // Per-tab footer: "Save & Next" (or "Create tenant" on the last tab, which
  // has its own action, so this renders only for non-review tabs).
  const tabFooter = () => (
    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
      <Button variant="contained" onClick={handleSaveAndNext} disabled={saving}>
        {saving ? 'Saving…' : 'Save & Next'}
      </Button>
    </div>
  );

  // ---- Review & Submit summary -----------------------------------------
  // A read-only recap of what's on every other tab, grouped by tab, each group
  // headed with an edit (pencil) icon that jumps back to that tab.
  const dash = (v) => (v && String(v).trim() ? String(v) : '—');
  const summaryRow = (label, value) => (
    <div style={{ display: 'flex', gap: 8, fontSize: 13, padding: '3px 0' }}>
      <span style={{ color: '#666', minWidth: 170, flexShrink: 0 }}>{label}</span>
      <span style={{ color: '#222', wordBreak: 'break-word' }}>{value}</span>
    </div>
  );
  const summarySection = (tabKey, title, rows) => (
    <div style={{ border: '1px solid #e0e0e0', borderRadius: 8, padding: 16, marginBottom: 12 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 8,
        }}
      >
        <h4 style={{ fontSize: 14, fontWeight: 600, margin: 0, color: '#0f538d' }}>{title}</h4>
        <IconButton size="small" aria-label={`edit ${title}`} onClick={() => goToTab(tabKey)}>
          <EditOutlinedIcon fontSize="small" />
        </IconButton>
      </div>
      {rows}
    </div>
  );

  if (loading) {
    return (
      <div className="p-5">
        <div className="p-5 bg-white borderStyle">Loading draft…</div>
      </div>
    );
  }

  const allErrors = Object.entries(errors).filter(([, msg]) => msg);

  return (
    <div className="p-5">
      <div className="p-5 bg-white borderStyle">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 16,
          }}
        >
          <h2 className="text-lg font-semibold">Create Tenant</h2>
          <span style={{ fontSize: 12, color: '#888', minWidth: 120, textAlign: 'right' }}>
            {saving
              ? 'Saving…'
              : dirty
                ? 'Unsaved changes'
                : lastSavedAt
                  ? 'All changes saved'
                  : ''}
          </span>
        </div>

        <TabContext value={activeTab}>
          <Box className="tabBoxStyle" sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <TabList
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              aria-label="tenant form tabs"
              sx={{
                minHeight: 48,
                // Natural-width tabs: each sizes to its FULL label (no
                // truncation). When all six don't fit the width, the auto
                // scroll chevron appears — the tabs never distort.
                '& .MuiTab-root': {
                  minHeight: 48,
                  minWidth: 'auto',
                  px: 2.5,
                  textTransform: 'none',
                  fontSize: 13,
                  whiteSpace: 'nowrap',
                },
              }}
            >
              {TENANT_FORM_TABS.map((tab) => (
                <Tab key={tab.key} label={tabLabel(tab)} value={tab.key} className="tabStyle" />
              ))}
            </TabList>
          </Box>

          {/* IDENTITY */}
          <TabPanel value="identity" className="tabPanelStyle">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                {textField('name', 'Name', { required: true })}
              </Grid>
              <Grid item xs={12} sm={6}>
                {textField('subdomain', 'Subdomain (optional)', {
                  placeholder: 'acme',
                  hint: 'Leave blank for the shared domain',
                })}
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  size="small"
                  options={globalCities}
                  getOptionLabel={(city) => city.name}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={selectedCities}
                  onChange={(event, value) => {
                    setSelectedCities(value);
                    markDirty();
                  }}
                  renderTags={() => null}
                  renderOption={(props, city) => (
                    <li {...props} key={city.id}>
                      {city.name}
                      {city.state ? ` — ${city.state} (GST ${city.stateGst})` : ''}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Operating Cities"
                      placeholder={globalCities.length ? 'Search or pick a city…' : 'Loading…'}
                      helperText={
                        citiesLoadError ||
                        errors.cities ||
                        'Pick from the platform city canon — a vendor company is created for each'
                      }
                      error={Boolean(citiesLoadError || errors.cities)}
                      inputRef={registerRef('cities')}
                    />
                  )}
                  disabled={!globalCities.length && !citiesLoadError}
                />
                {selectedCities.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                    {selectedCities.map((city) => (
                      <Chip
                        key={city.id}
                        size="small"
                        label={`${city.name}${city.state ? ` (${city.state})` : ''}`}
                        onDelete={() => {
                          setSelectedCities((prev) => prev.filter((c) => c.id !== city.id));
                          markDirty();
                        }}
                      />
                    ))}
                  </div>
                )}
              </Grid>
            </Grid>
            {tabFooter()}
          </TabPanel>

          {/* BRANDING */}
          <TabPanel value="branding" className="tabPanelStyle">
            <Grid container spacing={2}>
              <Grid item xs={12} md={7}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    {colorField('primaryColor', 'Primary Color')}
                  </Grid>
                  <Grid item xs={12}>
                    {colorField('secondaryColor', 'Secondary Color')}
                  </Grid>
                  <Grid item xs={12}>
                    {colorField('accentColor', 'Accent Color')}
                  </Grid>
                  <Grid item xs={12}>
                    <p style={{ fontSize: 12, color: '#666', margin: 0 }}>
                      Colors are optional — leaving them keeps the platform default palette shown in
                      the preview.
                    </p>
                  </Grid>
                  <Grid item xs={12}>
                    <div
                      style={{ display: 'flex', alignItems: 'center', gap: 12 }}
                      ref={registerRef('logo')}
                    >
                      {logo?.url ? (
                        <img
                          src={logo.url}
                          alt="tenant logo preview"
                          style={{
                            height: 40,
                            width: 40,
                            objectFit: 'contain',
                            borderRadius: 6,
                            border: '1px solid #e0e0e0',
                            background: '#fff',
                          }}
                        />
                      ) : null}
                      <Button
                        variant="outlined"
                        component="label"
                        size="small"
                        disabled={logoUploading}
                      >
                        {logoUploading ? 'Uploading…' : logo ? 'Change logo' : 'Upload logo'}
                        <input type="file" accept="image/*" hidden onChange={handleLogoSelect} />
                      </Button>
                      {logo ? (
                        <span style={{ fontSize: 12, color: '#666' }}>{logo.name}</span>
                      ) : (
                        <span style={{ fontSize: 12, color: errors.logo ? '#d32f2f' : '#666' }}>
                          {errors.logo ||
                            'Required — shown in the portal header and printed on invoices'}
                        </span>
                      )}
                    </div>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} md={5}>
                {preview()}
              </Grid>
            </Grid>
            {tabFooter()}
          </TabPanel>

          {/* BILLING / INVOICE */}
          <TabPanel value="billing" className="tabPanelStyle">
            <p style={{ fontSize: 12, color: '#666', margin: '0 0 12px' }}>
              Printed on every proforma/invoice this tenant issues. All fields are mandatory —
              invoice generation is blocked until they exist.
            </p>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                {textField('invLegalName', 'Legal Company Name', { required: true })}
              </Grid>
              <Grid item xs={12} sm={6}>
                {textField('invGstin', 'GSTIN', { required: true, placeholder: '22AAAAA0000A1Z5' })}
              </Grid>
              <Grid item xs={12} sm={6}>
                {textField('invPan', 'PAN', {
                  required: true,
                  placeholder: 'AAAAA0000A',
                  hint: 'Must match the PAN embedded in the GSTIN',
                })}
              </Grid>
              <Grid item xs={12} sm={6}>
                {textField('invRegisteredAddress', 'Registered Address', { required: true })}
              </Grid>
              <Grid item xs={12} sm={6}>
                {textField('invEmail', 'Billing Email', { required: true })}
              </Grid>
              <Grid item xs={12} sm={6}>
                {textField('invPhone', 'Billing Phone', { required: true })}
              </Grid>
              <Grid item xs={12} sm={6}>
                {textField('invWebsite', 'Website (optional)', { placeholder: 'www.example.com' })}
              </Grid>
              <Grid item xs={12} sm={6}>
                {textField('invJurisdiction', 'Legal Jurisdiction (city)', {
                  required: true,
                  hint: 'Shown as "disputes subject to <city> jurisdiction"',
                })}
              </Grid>
            </Grid>
            {tabFooter()}
          </TabPanel>

          {/* COMMUNICATIONS (SMTP) */}
          <TabPanel value="communications" className="tabPanelStyle">
            <p style={{ fontSize: 12, color: '#666', margin: '0 0 12px' }}>
              Optional. When configured, this tenant&apos;s emails are sent from their own mail
              server. Leave blank to use the platform&apos;s email identity. All fields are required
              together if any is filled. (On resume, re-enter the password to change it.)
            </p>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                {textField('smtpHost', 'SMTP Host', { placeholder: 'smtp.example.com' })}
              </Grid>
              <Grid item xs={12} sm={2}>
                {textField('smtpPort', 'Port', { placeholder: '587' })}
              </Grid>
              <Grid item xs={12} sm={4}>
                {textField('smtpUsername', 'SMTP Username')}
              </Grid>
              <Grid item xs={12} sm={6}>
                {textField('smtpPassword', 'SMTP Password', { type: 'password' })}
              </Grid>
              <Grid item xs={12} sm={6}>
                {textField('smtpFromEmail', 'From Email', { placeholder: 'billing@tenant.com' })}
              </Grid>
              <Grid item xs={12} sm={6}>
                {textField('smtpFromName', 'From Name (display)', {
                  hint: 'Optional — defaults to the tenant name',
                })}
              </Grid>
            </Grid>
            {tabFooter()}
          </TabPanel>

          {/* ROLES & FIRST ADMIN */}
          <TabPanel value="roles" className="tabPanelStyle">
            <h3 style={{ fontSize: 14, fontWeight: 600, margin: '0 0 4px' }}>Role Management</h3>
            <p style={{ fontSize: 12, color: '#666', margin: '0 0 4px' }}>
              May this tenant&apos;s Admin edit the tenant&apos;s roles and permissions? The tenant
              runs on the platform&apos;s standard roles either way.
            </p>
            <RadioGroup
              row
              value={form.roleManagementEnabled}
              onChange={handleChange('roleManagementEnabled')}
            >
              <FormControlLabel
                value="no"
                control={<Radio size="small" />}
                label="No — platform-managed roles only (default)"
              />
              <FormControlLabel
                value="yes"
                control={<Radio size="small" />}
                label="Yes — tenant admin may manage roles"
              />
            </RadioGroup>
            <h3 style={{ fontSize: 14, fontWeight: 600, margin: '16px 0 4px' }}>Admins</h3>
            <p style={{ fontSize: 12, color: '#666', margin: '0 0 12px' }}>
              Each admin is created with the Admin role — full access, and can create every further
              user from user management. Add as many as you need.
            </p>
            {form.admins.map((admin, index) => (
              <div
                key={index}
                style={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 8,
                  padding: 16,
                  marginBottom: 12,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 8,
                  }}
                >
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#0f538d' }}>
                    Admin {index + 1}
                    {index === 0 ? ' (first admin)' : ''}
                  </span>
                  {form.admins.length > 1 && (
                    <IconButton
                      size="small"
                      aria-label={`remove admin ${index + 1}`}
                      onClick={() => removeAdmin(index)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  )}
                </div>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    {adminField(index, 'firstName', 'Admin First Name', { required: true })}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    {adminField(index, 'lastName', 'Admin Last Name')}
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    {adminField(index, 'countryCode', 'Country Code')}
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    {adminField(index, 'mobile', 'Admin Mobile', { required: true })}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    {adminField(index, 'email', 'Admin Email', {
                      required: true,
                      hint: 'The login credential for the portal',
                    })}
                  </Grid>
                </Grid>
              </div>
            ))}
            <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={addAdmin}>
              Add another admin
            </Button>
            {tabFooter()}
          </TabPanel>

          {/* REVIEW & SUBMIT */}
          <TabPanel value="review" className="tabPanelStyle">
            <h3 style={{ fontSize: 14, fontWeight: 600, margin: '0 0 12px' }}>
              Review &amp; submit
            </h3>

            {/* Grouped recap of every other tab; the pencil jumps back to edit. */}
            {summarySection(
              'identity',
              'Identity',
              <>
                {summaryRow('Name', dash(form.name))}
                {summaryRow(
                  'Subdomain',
                  dash(form.subdomain) === '—' ? 'Shared domain' : form.subdomain,
                )}
                {summaryRow(
                  'Operating cities',
                  selectedCities.length ? selectedCities.map((c) => c.name).join(', ') : '—',
                )}
              </>,
            )}

            {summarySection(
              'branding',
              'Branding',
              <>
                {summaryRow('Logo', logo?.name ? logo.name : 'Not uploaded')}
                {summaryRow('Primary color', dash(form.primaryColor))}
                {summaryRow('Secondary color', dash(form.secondaryColor))}
                {summaryRow('Accent color', dash(form.accentColor))}
              </>,
            )}

            {summarySection(
              'billing',
              'Billing / Invoice',
              <>
                {summaryRow('Legal company name', dash(form.invLegalName))}
                {summaryRow('GSTIN', dash(form.invGstin))}
                {summaryRow('PAN', dash(form.invPan))}
                {summaryRow('Registered address', dash(form.invRegisteredAddress))}
                {summaryRow('Billing email', dash(form.invEmail))}
                {summaryRow('Billing phone', dash(form.invPhone))}
                {summaryRow('Website', dash(form.invWebsite))}
                {summaryRow('Legal jurisdiction', dash(form.invJurisdiction))}
              </>,
            )}

            {summarySection(
              'communications',
              'Communications',
              form.smtpHost.trim() ? (
                <>
                  {summaryRow('SMTP host', dash(form.smtpHost))}
                  {summaryRow('Port', dash(form.smtpPort))}
                  {summaryRow('Username', dash(form.smtpUsername))}
                  {summaryRow('From email', dash(form.smtpFromEmail))}
                  {summaryRow('From name', dash(form.smtpFromName))}
                </>
              ) : (
                summaryRow('Email (SMTP)', 'Not configured — platform email identity will be used')
              ),
            )}

            {summarySection(
              'roles',
              'Roles & Admin',
              <>
                {summaryRow(
                  'Role management',
                  form.roleManagementEnabled === 'yes'
                    ? 'Tenant admin may manage roles'
                    : 'Platform-managed roles only',
                )}
                {form.admins.map((a, i) =>
                  summaryRow(
                    `Admin ${i + 1}`,
                    [
                      [a.firstName, a.lastName].filter(Boolean).join(' '),
                      a.email,
                      a.mobile ? `${a.countryCode || ''} ${a.mobile}`.trim() : '',
                    ]
                      .filter(Boolean)
                      .join(' · ') || '—',
                  ),
                )}
              </>,
            )}

            {/* Outstanding issues (client-side) — each deep-links to its field. */}
            {allErrors.length === 0 ? (
              <p style={{ fontSize: 13, color: '#2fab73', margin: '4px 0 16px' }}>
                No outstanding issues found yet. Click Create tenant to run the full check and
                provision.
              </p>
            ) : (
              <div style={{ marginBottom: 16 }}>
                <p style={{ fontSize: 13, color: '#d32f2f', margin: '0 0 8px' }}>
                  The following need attention before this tenant can be created:
                </p>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {allErrors.map(([field, msg]) => (
                    <li key={field} style={{ fontSize: 13, marginBottom: 4 }}>
                      <button
                        type="button"
                        onClick={() => focusFirstError({ [field]: msg })}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#0f538d',
                          cursor: 'pointer',
                          padding: 0,
                          textDecoration: 'underline',
                          fontSize: 13,
                        }}
                      >
                        {TENANT_FORM_TABS.find((t) => t.key === tabForField(field))?.label ||
                          'Field'}
                        : {msg}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
                {submitting ? 'Creating…' : 'Create tenant'}
              </Button>
            </div>
          </TabPanel>
        </TabContext>
      </div>
    </div>
  );
}

export default TenantFormTabs;
